<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import instagramClient from 'dashboard/api/channel/instagramClient';
import PageHeader from '../../SettingsSubPageHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const isLoading = ref(true);
const isCreating = ref(false);
const oauthUrl = ref('');
const accounts = ref([]);
const selectedAccountId = ref('');
const manualUsername = ref('');
const inboxName = ref('');
const isManualMode = ref(false);

const selectedAccount = computed(() => {
  return accounts.value.find(acc => acc.id === selectedAccountId.value) || null;
});

const selectAccount = account => {
  selectedAccountId.value = account.id;
  manualUsername.value = account.username || '';
  if (!inboxName.value || accounts.value.some(a => a.username === inboxName.value)) {
    inboxName.value = account.username ? `@${account.username}` : 'Instagram Support';
  }
};

const fetchServiceData = async () => {
  isLoading.value = true;
  try {
    const [infoRes, accountsRes] = await Promise.allSettled([
      instagramClient.getServiceInfo(),
      instagramClient.getAccounts(),
    ]);

    if (infoRes.status === 'fulfilled' && infoRes.value?.data) {
      const data = infoRes.value.data.data || infoRes.value.data;
      oauthUrl.value = data.instagram_oauth_url || '';
    }

    if (accountsRes.status === 'fulfilled' && accountsRes.value?.data) {
      const fetchedAccounts = accountsRes.value.data.data || accountsRes.value.data;
      if (Array.isArray(fetchedAccounts) && fetchedAccounts.length) {
        accounts.value = fetchedAccounts;
        selectAccount(fetchedAccounts[0]);
      }
    }
  } catch (err) {
    // Ignore errors silently on initial fetch
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchServiceData();
});

const startOAuthLogin = () => {
  if (oauthUrl.value) {
    window.location.href = oauthUrl.value;
  } else {
    useAlert(t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_AUTH'));
  }
};

const createInbox = async () => {
  const account = selectedAccount.value;
  const username = account ? account.username : manualUsername.value.replace(/^@/, '').trim();

  if (!username) {
    useAlert(t('INBOX_MGMT.ADD.INSTAGRAM.INSTAGRAM_ID_LABEL'));
    return;
  }

  if (!inboxName.value.trim()) {
    useAlert(t('INBOX_MGMT.ADD.INSTAGRAM.INBOX_NAME_PLACEHOLDER'));
    return;
  }

  isCreating.value = true;
  try {
    const channelData = {
      name: inboxName.value.trim(),
      channel: {
        type: 'instagram',
        username: username,
        instagram_id: account?.instagram_user_id || username,
        boxapi_account_id: account?.id || null,
        expires_at: account?.expires_at || null,
      },
    };

    const inbox = await store.dispatch('inboxes/createChannel', channelData);

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: inbox.id,
      },
    });
  } catch (error) {
    const msg =
      error.response?.data?.message ||
      error.message ||
      t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_MESSAGE');
    useAlert(msg);
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 overflow-y-auto">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.INSTAGRAM.CONNECT_YOUR_INSTAGRAM_PROFILE')"
      :header-content="$t('INBOX_MGMT.ADD.INSTAGRAM.HELP')"
    />

    <LoadingState v-if="isLoading" message="در حال بارگذاری اطلاعات اتصال..." />

    <!-- State 1: No connected accounts yet - Direct Official OAuth Login Button -->
    <div
      v-else-if="!accounts.length && !isManualMode"
      class="flex flex-col items-center justify-center py-12 max-w-lg mx-auto text-center gap-6"
    >
      <div class="w-16 h-16 rounded-2xl bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 flex items-center justify-center shadow-lg shadow-pink-500/20">
        <i class="i-ri-instagram-line text-white text-3xl" />
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="text-xl font-bold text-n-slate-12">
          اتصال حساب رسمی اینستاگرام
        </h3>
        <p class="text-sm text-n-slate-11 leading-relaxed">
          جهت اتصال دایرکت‌ها و پاسخگویی به پیام‌های مخاطبان، با حساب اینستاگرام خود وارد شوید و دسترسی رسمی را تایید کنید.
        </p>
      </div>

      <div class="flex flex-col items-center gap-3 w-full max-w-xs">
        <button
          type="button"
          class="w-full py-3 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 via-rose-500 to-purple-600 hover:opacity-95 shadow-md flex items-center justify-center gap-2 transition-all cursor-pointer"
          @click="startOAuthLogin"
        >
          <i class="i-ri-instagram-line text-lg" />
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.CONTINUE_WITH_INSTAGRAM') }}
        </button>

        <button
          type="button"
          class="text-xs text-n-slate-10 hover:text-n-slate-12 underline mt-2 cursor-pointer"
          @click="isManualMode = true"
        >
          یا ثبت مستقیم نام کاربری
        </button>
      </div>
    </div>

    <!-- State 2: Connected Account(s) found OR Manual Mode -->
    <div v-else class="max-w-xl mt-4">
      <form class="flex flex-col gap-5" @submit.prevent="createInbox">
        <!-- Connected Account Selection -->
        <div v-if="accounts.length && !isManualMode">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-semibold text-n-slate-12">
              حساب متصل شده
            </h4>
            <button
              type="button"
              class="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer flex items-center gap-1"
              @click="startOAuthLogin"
            >
              <i class="i-ri-refresh-line" />
              اتصال یا تغییر حساب
            </button>
          </div>

          <div class="grid grid-cols-1 gap-3">
            <div
              v-for="account in accounts"
              :key="account.id"
              class="flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all border-blue-500 bg-blue-50/50 dark:bg-blue-950/30"
              @click="selectAccount(account)"
            >
              <div class="flex items-center gap-3">
                <img
                  v-if="account.profile_photo"
                  :src="account.profile_photo"
                  class="w-12 h-12 rounded-full object-cover border border-n-weak"
                  alt="Profile"
                />
                <div
                  v-else
                  class="w-12 h-12 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg"
                >
                  IG
                </div>
                <div>
                  <div class="font-bold text-n-slate-12 text-base">
                    @{{ account.username }}
                  </div>
                  <div class="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 mt-0.5">
                    <i class="i-ri-checkbox-circle-fill" />
                    حساب متصل و آماده استفاده
                  </div>
                </div>
              </div>

              <input
                type="radio"
                checked
                name="selected_account"
                class="text-blue-600 focus:ring-blue-500 w-4 h-4"
              />
            </div>
          </div>
        </div>

        <!-- Manual Username Input -->
        <label v-else class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.INSTAGRAM_ID_LABEL') }}
          <div class="relative flex items-center">
            <span class="absolute left-3 rtl:right-3 rtl:left-auto text-n-slate-10 font-bold select-none">
              @
            </span>
            <input
              v-model="manualUsername"
              type="text"
              class="input-box ltr:pl-8 rtl:pr-8 w-full"
              :placeholder="$t('INBOX_MGMT.ADD.INSTAGRAM.INSTAGRAM_ID_PLACEHOLDER')"
              autofocus
            />
          </div>
        </label>

        <!-- Inbox Name -->
        <label class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.INBOX_NAME') }}
          <input
            v-model="inboxName"
            type="text"
            class="input-box w-full"
            :placeholder="$t('INBOX_MGMT.ADD.INSTAGRAM.INBOX_NAME_PLACEHOLDER')"
          />
        </label>

        <!-- Submit Button -->
        <div class="flex items-center gap-3 mt-3">
          <Button
            type="submit"
            :label="$t('INBOX_MGMT.ADD.INSTAGRAM.CREATE_INBOX')"
            :disabled="(!selectedAccount && !manualUsername.trim()) || !inboxName.trim() || isCreating"
            :is-loading="isCreating"
            solid
            blue
            icon="i-ri-checkbox-circle-line"
          />

          <Button
            v-if="isManualMode"
            variant="ghost"
            label="بازگشت به ورود رسمی"
            @click="isManualMode = false"
          />
        </div>
      </form>
    </div>
  </div>
</template>
